import {
  ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation,
  effect, inject,
} from '@angular/core';
import { AppService } from '../../../core/app.service';

/**
 * "Voyago" — the idle mascot. When the user goes idle, Voyago strolls in with a ladder, climbs the
 * sidebar, borrows a random menu item (marking it {@code .stolen} — a global style in styles.scss),
 * scurries off, and after a short while brings it back with a toast. Purely decorative + inert:
 * pointer-events none, no listeners at all until enabled (SYSTEM → Settings toggle, admin-gated).
 *
 * <p>Runs only while {@code AppService.mascotEnabled()} is true and the user hasn't asked for reduced
 * motion. Sidebar-side aware (works whether the rail is on the left (LTR) or right (RTL)). Sprite is
 * pure CSS; ported from the Voyage design mock.
 */
@Component({
  selector: 'app-voyago-mascot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  template: `
    <div class="vg-stage" aria-hidden="true">
      <div #ladder class="vg-ladder"></div>
      <div #voyage class="vg-voyage">
        <div class="flip">
          <div class="v-glow"></div>
          <div class="v-pack"></div>
          <div class="v-tie"></div>
          <div class="v-arm back"></div>
          <div class="v-body"></div>
          <div class="v-leg a"></div>
          <div class="v-leg b"></div>
          <div class="v-face">
            <span class="hair"></span>
            <span class="mask"></span><span class="hole a"></span><span class="hole b"></span>
            <span class="eye a"></span><span class="eye b"></span>
            <span class="cheek a"></span><span class="cheek b"></span><span class="nose"></span>
          </div>
          <div class="v-arm front"></div>
        </div>
        <div class="vg-firefly"></div>
        <div #loot class="vg-loot"></div>
        <div #speech class="vg-speech"></div>
      </div>
    </div>
  `,
  styleUrl: './voyago-mascot.component.scss',
})
export class VoyagoMascotComponent implements OnDestroy {
  private readonly app = inject(AppService);

  @ViewChild('voyage') private voyageRef!: ElementRef<HTMLElement>;
  @ViewChild('ladder') private ladderRef!: ElementRef<HTMLElement>;
  @ViewChild('loot') private lootRef!: ElementRef<HTMLElement>;
  @ViewChild('speech') private speechRef!: ElementRef<HTMLElement>;

  private get voy() { return this.voyageRef?.nativeElement; }
  private get lad() { return this.ladderRef?.nativeElement; }
  private get loot() { return this.lootRef?.nativeElement; }
  private get speech() { return this.speechRef?.nativeElement; }

  /** Timings/speed come from SYSTEM → Settings (persisted signals), read live. */
  private idleMs(): number { return Math.max(3, this.app.mascotIdle()) * 1000; }
  private awayMs(): number { return Math.max(3, this.app.mascotAway()) * 1000; }
  /** Scale a motion duration by the speed multiplier (higher speed → shorter). */
  private dur(ms: number): number { return Math.max(1, Math.round(ms / (this.app.mascotSpeed() || 1))); }

  private busy = false;
  private interrupted = false;
  private stolenEl: HTMLElement | null = null;
  private currentLabel = '';
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private listening = false;
  /** While idle → (re)arm the idle timer. While a heist is running → flag it to
   *  abort/retreat: Voyago only plays while the user is actually idle. */
  private readonly onActivity = () => { if (this.busy) this.interrupted = true; else this.armIdle(); };

  constructor() {
    effect(() => {
      if (this.app.mascotEnabled() && !this.reducedMotion()) this.enable();
      else this.disable();
    });
  }

  ngOnDestroy(): void { this.disable(); }

  // ── enable / disable ──────────────────────────────────────────────────────
  private reducedMotion(): boolean {
    return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private enable(): void {
    if (this.listening) return;
    this.listening = true;
    for (const e of ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']) {
      window.addEventListener(e, this.onActivity, { passive: true });
    }
    this.armIdle();
  }

  private disable(): void {
    if (this.listening) {
      for (const e of ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']) {
        window.removeEventListener(e, this.onActivity);
      }
      this.listening = false;
    }
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.reset();
  }

  private armIdle(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.busy) return;
    this.idleTimer = setTimeout(() => this.heist(), this.idleMs());
  }

  private reset(): void {
    if (this.stolenEl) { this.stolenEl.classList.remove('stolen'); this.stolenEl = null; }
    this.hideLoot();
    this.hush();
    this.voy?.classList.remove('carrying', 'walking', 'climb', 'shuffle', 'reach', 'face-left');
    if (this.voy) this.voy.style.opacity = '0';
    if (this.lad) this.lad.style.opacity = '0';
    this.busy = false;
  }

  // ── helpers ───────────────────────────────────────────────────────────────
  private wait(ms: number): Promise<void> { return new Promise((r) => setTimeout(r, ms)); }

  /** Like wait(), but resolves early the moment the user becomes active (interrupted)
   *  or the heist is otherwise ended — so Voyago reacts promptly to you coming back. */
  private waitI(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const step = 120; let elapsed = 0;
      const tick = () => {
        if (this.interrupted || !this.busy || elapsed >= ms) return resolve();
        elapsed += step; setTimeout(tick, step);
      };
      setTimeout(tick, Math.min(step, ms));
    });
  }

  /** User came back mid-approach → abandon the heist and scurry off (nothing taken). */
  private async retreat(g: { exitX: number; faceOut: number }): Promise<void> {
    const v = this.voy;
    this.hush();
    if (this.lad) this.lad.style.opacity = '0';
    if (v) {
      v.classList.remove('climb', 'shuffle', 'reach', 'carrying');
      v.classList.add('walking'); this.face(g.faceOut);
      this.setX(v, g.exitX, 0, this.dur(700));
      await this.wait(this.dur(750));
    }
    this.reset();
    this.armIdle();
  }

  private setX(el: HTMLElement, x: number, bottom: number, ms: number): void {
    el.style.transition = ms ? `transform ${ms}ms cubic-bezier(.37,.01,.4,1)` : 'none';
    el.style.transform = `translate(${x}px, ${-(bottom || 0)}px)`;
  }

  private face(dir: number): void { this.voy?.classList.toggle('face-left', dir < 0); }
  private say(t: string): void { if (this.speech) { this.speech.textContent = t; this.speech.classList.add('show'); } }
  private hush(): void { this.speech?.classList.remove('show'); }

  /** Sidebar-side-aware geometry. Returns null if the sidebar is hidden/collapsed. */
  private geometry() {
    const sb = document.querySelector('.sidebar') as HTMLElement | null;
    const VW = window.innerWidth, VH = window.innerHeight;
    if (!sb) return null;
    const r = sb.getBoundingClientRect();
    if (r.width < 8) return null;
    const left = r.left < VW / 2;   // rail on the left (LTR) vs right (RTL)
    const ladderX = left ? r.right - 4 : r.left - 30;
    return {
      VW, VH,
      parkX: left ? r.right + 6 : r.left - 62,
      ladderX,
      // Sprite left so its body-centre (spriteLeft+28) sits on the ladder centre
      // (ladderX+17) — otherwise he climbs beside the ladder, looking mid-air.
      climbX: ladderX - 11,
      enterX: left ? VW + 80 : -220,
      exitX: left ? VW + 90 : -220,
      faceIn: left ? -1 : 1,
      faceOut: left ? 1 : -1,
    };
  }

  private pickTarget(): HTMLElement | null {
    const nodes = [...document.querySelectorAll<HTMLElement>('.sidebar-nav .nav-item:not(.active):not(.stolen)')]
      .filter((el) => el.offsetParent !== null);   // visible (section not collapsed)
    if (!nodes.length) return null;
    return nodes[Math.floor(Math.random() * nodes.length)];
  }

  private buildLadder(h: number): void {
    if (!this.lad) return;
    const height = Math.max(70, h);
    this.lad.style.height = height + 'px';
    this.lad.innerHTML = '<div class="rail l"></div><div class="rail r"></div>';
    const n = Math.floor(height / 16);
    for (let i = 1; i < n; i++) {
      const rung = document.createElement('div');
      rung.className = 'rung';
      rung.style.bottom = (i * 16) + 'px';
      this.lad.appendChild(rung);
    }
  }

  private showLoot(target: HTMLElement): void {
    if (!this.loot) return;
    const icon = target.querySelector('.nav-icon');
    const label = this.labelOf(target);
    this.currentLabel = label;
    this.loot.innerHTML = (icon ? icon.innerHTML : '') + '<span>' + this.escape(label) + '</span>';
    this.loot.classList.add('show');
  }
  private hideLoot(): void { this.loot?.classList.remove('show'); }

  private labelOf(target: HTMLElement): string {
    const span = [...target.querySelectorAll('span')]
      .find((s) => !s.classList.contains('nav-icon') && !s.classList.contains('nav-badge'));
    return (span?.textContent ?? '').trim();
  }
  private escape(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  // ── the heist ─────────────────────────────────────────────────────────────
  private async heist(): Promise<void> {
    if (this.busy || !this.voy) return;
    const g = this.geometry();
    const target = this.pickTarget();
    if (!g || !target) { this.armIdle(); return; }
    this.busy = true;
    this.interrupted = false;

    const r = target.getBoundingClientRect();
    const itemBottom = g.VH - r.bottom;
    const climbBottom = itemBottom + r.height / 2 - 26;

    // 1) stroll in toward the rail
    this.setX(this.voy, g.enterX, 0, 0);
    this.face(g.faceIn); this.voy.classList.add('walking'); this.voy.style.opacity = '1';
    await this.wait(this.dur(30));
    this.setX(this.voy, g.parkX + (g.faceIn < 0 ? 40 : -40), 0, this.dur(2200)); await this.waitI(this.dur(2200));
    if (this.interrupted) return this.retreat(g);   // user is back — leave without taking anything

    // 2) set the ladder
    this.buildLadder(itemBottom + r.height / 2 + 16);
    this.setX(this.lad, g.ladderX, 0, 0); this.lad.style.opacity = '1';
    this.say('borrowing this…'); await this.wait(this.dur(700)); this.hush();

    // 3) climb
    this.voy.classList.remove('walking'); this.voy.classList.add('climb', 'shuffle');
    this.setX(this.voy, g.climbX, climbBottom, this.dur(1400)); await this.waitI(this.dur(1450));
    this.voy.classList.remove('shuffle');
    if (this.interrupted) return this.retreat(g);   // still nothing taken — bail out

    // 4) reach + grab, then stash it into the saddlebag (bag fills + glows)
    this.voy.classList.add('reach'); await this.wait(this.dur(340));
    target.classList.add('stolen'); this.stolenEl = target;
    this.showLoot(target); await this.wait(this.dur(620));
    this.hideLoot(); this.voy.classList.add('carrying'); await this.wait(this.dur(320));
    this.voy.classList.remove('reach');

    // 5) climb down
    this.voy.classList.add('shuffle');
    this.setX(this.voy, g.climbX, 0, this.dur(1200)); await this.wait(this.dur(1250));
    this.voy.classList.remove('climb', 'shuffle');

    // 6) take the ladder + scurry off with the loot
    this.lad.style.opacity = '0'; this.face(g.faceOut); this.voy.classList.add('walking');
    this.say('brb!');
    this.setX(this.voy, g.exitX, 0, this.dur(1800)); await this.wait(this.dur(900)); this.hush(); await this.wait(this.dur(900));
    this.voy.classList.remove('walking'); this.voy.style.opacity = '0';

    // 7) wait away — but if the user comes back, cut it short and return the menu now
    await this.waitI(this.awayMs());
    await this.returnIt();
    this.busy = false;
    this.armIdle();
  }

  private async returnIt(): Promise<void> {
    const target = this.stolenEl;
    if (!this.voy || !target || !target.isConnected) { this.reset(); return; }
    const g = this.geometry();
    if (!g) { this.reset(); return; }

    const r = target.getBoundingClientRect();
    const itemBottom = g.VH - r.bottom;
    const climbBottom = itemBottom + r.height / 2 - 26;

    this.setX(this.voy, g.enterX, 0, 0); this.face(g.faceIn); this.voy.style.opacity = '1'; this.voy.classList.add('walking');
    await this.wait(this.dur(30));
    this.setX(this.voy, g.parkX + (g.faceIn < 0 ? 40 : -40), 0, this.dur(2000)); await this.wait(this.dur(2000));
    this.buildLadder(itemBottom + r.height / 2 + 16); this.setX(this.lad, g.ladderX, 0, 0); this.lad.style.opacity = '1';
    this.voy.classList.remove('walking'); this.voy.classList.add('climb', 'shuffle');
    this.setX(this.voy, g.climbX, climbBottom, this.dur(1300)); await this.wait(this.dur(1350)); this.voy.classList.remove('shuffle');
    // take it out of the full bag and set it back (bag empties)
    this.voy.classList.add('reach'); await this.wait(this.dur(300));
    this.voy.classList.remove('carrying'); this.showLoot(target); await this.wait(this.dur(340));
    this.hideLoot(); target.classList.remove('stolen'); this.stolenEl = null;
    target.animate([{ transform: 'translateX(-6px)' }, { transform: 'translateX(0)' }], { duration: 300, easing: 'ease-out' });
    this.say('good as new ✦'); await this.wait(this.dur(650)); this.voy.classList.remove('reach');
    this.voy.classList.add('shuffle'); this.setX(this.voy, g.climbX, 0, this.dur(1100)); await this.wait(this.dur(1150));
    this.voy.classList.remove('climb', 'shuffle'); this.hush();
    this.lad.style.opacity = '0'; this.face(g.faceOut); this.voy.classList.add('walking');
    this.setX(this.voy, g.exitX, 0, this.dur(1700)); await this.wait(this.dur(1700));
    this.voy.classList.remove('walking'); this.voy.style.opacity = '0';

    this.app.toastKey('finance.settings.mascotReturned', { label: this.currentLabel });
  }
}
